import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/authSlice';
import { getDashboardStats, getBranchStats, getRecentActivities } from '../store/statsSlice';
import { addSale, getMySales, getAllSales, updateSale, deleteSale } from '../store/saleSlice';
import { getServices } from '../store/serviceSlice';
import { useToastWithSound } from '../hooks/useToastWithSound';
import Sidebar from '../components/Sidebar';
import ModernHeader from '../components/ModernHeader';
import DashboardStats from '../components/DashboardStats';
import DashboardOverview from '../components/DashboardOverview';
import UserManagement from '../components/admin/UserManagement';
import ServiceManagement from '../components/admin/ServiceManagement';
import SalesReports from '../components/SalesReports';

import { FaChartBar, FaBell } from 'react-icons/fa';

const AdminDashboardScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  const [serviceId, setServiceId] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [saleAmount, setSaleAmount] = useState('');
  const [profit, setProfit] = useState('');
  const [showSalePopup, setShowSalePopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingSaleId, setDeletingSaleId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showActionToast, showError } = useToastWithSound();
  const { userInfo } = useSelector((state) => state.auth);
  const { services, loading: servicesLoading, error: servicesError } = useSelector((state) => state.services);
  const { sales, allSales, pagination, loading: salesLoading, allSalesLoading, error: salesError } = useSelector((state) => state.sales);

  // Function to refresh dashboard data
  const refreshDashboardData = () => {
    dispatch(getDashboardStats());
    dispatch(getBranchStats());
    dispatch(getRecentActivities());
  };

  // Handle tab change and refresh data when switching to dashboard
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab }); // Update URL to maintain state on reload
    if (newTab === 'dashboard') {
      refreshDashboardData();
    } else if (newTab === 'sales') {
      dispatch(getAllSales({ page: currentPage, limit: recordsPerPage }));
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(getAllSales({ page, limit: recordsPerPage }));
  };

  // Handle records per page change
  const handleRecordsPerPageChange = (limit) => {
    setRecordsPerPage(limit);
    setCurrentPage(1);
    dispatch(getAllSales({ page: 1, limit }));
  };

  // Handle service selection
  const handleServiceSelect = (service) => {
    setServiceId(service._id);
    setServiceName(service.name);
    setServiceSearch(service.name);
    setShowServiceDropdown(false);
  };

  // Submit sale handler
  const submitHandler = (e) => {
    e.preventDefault();
    
    if (!serviceId || !saleAmount || !profit) {
      showActionToast('validationError', false, 'Please fill all required fields');
      return;
    }
    
    const saleData = {
      serviceId,
      price: parseFloat(saleAmount),
      profit: parseFloat(profit),
      cost: parseFloat(saleAmount) - parseFloat(profit),
      branch: 'Admin', // Admin can add sales for any branch
      customerName: 'Walk-in Customer',
      customerMobile: '0000000000'
    };
    
    dispatch(addSale(saleData))
      .unwrap()
      .then(() => {
        showActionToast('addSale', true, 'Sale added successfully!');
        // Reset form
        setServiceId('');
        setServiceName('');
        setServiceSearch('');
        setSaleAmount('');
        setProfit('');
        setShowSalePopup(false);
        // Refresh sales list
        dispatch(getAllSales({ page: currentPage, limit: recordsPerPage }));
      })
      .catch((error) => {
        showActionToast('addSale', false, error.message || 'Failed to add sale');
      });
  };

  // Handle edit sale
  const handleEditSale = (sale) => {
    setEditingSale(sale);
    setServiceId(sale.service?._id || '');
    setServiceName(sale.service?.name || '');
    setServiceSearch(sale.service?.name || '');
    setSaleAmount(sale.price?.toString() || '');
    setProfit(sale.profit?.toString() || '');
    setShowEditPopup(true);
  };

  // Submit edit sale handler
  const submitEditHandler = (e) => {
    e.preventDefault();
    
    if (!serviceId || !saleAmount || !profit) {
      showActionToast('validationError', false, 'Please fill all required fields');
      return;
    }
    
    const saleData = {
      serviceId,
      price: parseFloat(saleAmount),
      profit: parseFloat(profit),
      cost: parseFloat(saleAmount) - parseFloat(profit)
    };
    
    dispatch(updateSale({ id: editingSale._id, saleData }))
      .unwrap()
      .then(() => {
        showActionToast('updateSale', true, 'Sale updated successfully!');
        // Reset form
        setServiceId('');
        setServiceName('');
        setServiceSearch('');
        setSaleAmount('');
        setProfit('');
        setEditingSale(null);
        setShowEditPopup(false);
        // Refresh sales list
        dispatch(getAllSales({ page: currentPage, limit: recordsPerPage }));
      })
      .catch((error) => {
        showActionToast('updateSale', false, error.message || 'Failed to update sale');
      });
  };

  // Handle delete sale
  const handleDeleteSale = (saleId) => {
    setDeletingSaleId(saleId);
    setShowDeleteConfirm(true);
  };

  // Confirm delete sale
  const confirmDeleteSale = () => {
    dispatch(deleteSale(deletingSaleId))
      .unwrap()
      .then(() => {
        showActionToast('deleteSale', true, 'Sale deleted successfully!');
        setShowDeleteConfirm(false);
        setDeletingSaleId(null);
        // Refresh sales list
        dispatch(getAllSales({ page: currentPage, limit: recordsPerPage }));
      })
      .catch((error) => {
        showActionToast('deleteSale', false, error.message || 'Failed to delete sale');
      });
  };



  useEffect(() => {
    // Check if user is authenticated and has admin role
    if (!userInfo || !userInfo.token) {
      navigate('/login');
      return;
    }
    
    if (userInfo.role !== 'Admin') {
      navigate('/staff-dashboard');
      return;
    }

    // Check token expiration
    checkTokenExpiration();
    
    // Load dashboard data on initial load
    if (activeTab === 'dashboard') {
      refreshDashboardData();
    }
    
    // Load services for sales management
    dispatch(getServices());
  }, [userInfo, navigate, activeTab]);

  // Load sales data when sales tab is active
  useEffect(() => {
    if (activeTab === 'sales' && userInfo && userInfo.token) {
      dispatch(getAllSales({ page: currentPage, limit: recordsPerPage }));
    }
  }, [activeTab, currentPage, recordsPerPage, dispatch, userInfo]);

  const checkTokenExpiration = () => {
    if (userInfo && userInfo.token) {
      try {
        const tokenPayload = JSON.parse(atob(userInfo.token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (tokenPayload.exp < currentTime) {
          handleLogout();
        }
      } catch (error) {
        console.error('Token validation error:', error);
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-8">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 rounded-2xl p-8 text-white shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-4xl font-bold mb-2">Welcome back, Admin! 👋</h1>
                  <p className="text-blue-100 text-lg">Here's what's happening with your business today</p>
                  <p className="text-blue-200 text-sm mt-2">{new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</p>
                </div>
                <div className="hidden md:block">
                  <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-4xl">📊</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dashboard Stats */}
            <DashboardStats userRole={userInfo?.role || 'Admin'} />

            {/* Dashboard Overview */}
            <DashboardOverview userRole={userInfo?.role || 'Admin'} />
          </div>
        );
      case 'users':
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">User Management</h2>
            <UserManagement />
          </div>
        );
      case 'services':
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Service Management</h2>
            <ServiceManagement />
          </div>
        );
      case 'sales':
        return (
          <div className="space-y-8">
            {/* Sales Management Header */}
            <div className="bg-gradient-to-r from-green-600 to-blue-700 text-white p-6 rounded-2xl shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold mb-2">Sales Management 🛒</h1>
                  <p className="text-green-100">Add new sales and manage all transactions</p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowSalePopup(true)}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg transform hover:scale-105 backdrop-blur-sm border border-white border-opacity-30"
                  >
                    + Add New Sale
                  </button>
                  <div className="hidden md:block">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <span className="text-2xl">💰</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales Table */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">All Sales Records</h3>
                    <p className="text-gray-600 mt-1">Manage and view all sales transactions</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Show:</span>
                      <select
                        value={recordsPerPage}
                        onChange={(e) => handleRecordsPerPageChange(parseInt(e.target.value))}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-sm text-gray-600">entries</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale Amount</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profit</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allSalesLoading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center">
                          <div className="flex items-center justify-center">
                            <svg className="animate-spin h-6 w-6 text-blue-500 mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading sales...
                          </div>
                        </td>
                      </tr>
                    ) : allSales && allSales.length > 0 ? (
                      allSales.map((sale) => (
                        <tr key={sale._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
                                <span className="text-white text-xs font-bold">{sale.service?.name?.charAt(0) || 'S'}</span>
                              </div>
                              <span className="font-medium text-gray-900">{sale.service?.name || 'Unknown Service'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                              ₹{sale.price?.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                              ₹{sale.profit?.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center mr-3">
                                <span className="text-white text-xs font-bold">{sale.user?.name?.charAt(0) || 'U'}</span>
                              </div>
                              <span className="font-medium text-gray-900">{sale.user?.name || 'Unknown User'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {sale.branch}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              <div className="font-medium">{new Date(sale.createdAt).toLocaleDateString('en-IN')}</div>
                              <div className="text-sm text-gray-500">{new Date(sale.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditSale(sale)}
                                className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center space-x-1"
                                title="Edit Sale"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleDeleteSale(sale._id)}
                                className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center space-x-1"
                                title="Delete Sale"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span>Delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center">
                          <div className="text-gray-500">
                            <span className="text-4xl mb-4 block">📊</span>
                            <p className="text-lg font-medium">No sales records found</p>
                            <p className="text-sm">Add your first sale to get started</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {allSales && allSales.length > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.currentPage - 1) * pagination.recordsPerPage) + 1} to {Math.min(pagination.currentPage * pagination.recordsPerPage, pagination.totalRecords)} of {pagination.totalRecords} entries
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {/* Page Numbers */}
                      {[...Array(pagination.totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        const isCurrentPage = pageNumber === pagination.currentPage;
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`px-3 py-2 text-sm font-medium rounded-md ${
                              isCurrentPage
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Add Sale Modal */}
            {showSalePopup && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-800">Add New Sale</h3>
                    <button
                      onClick={() => setShowSalePopup(false)}
                      className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                    >
                      ×
                    </button>
                  </div>
                  
                  <form onSubmit={submitHandler} className="space-y-4">
                    {/* Service Selection */}
                    <div className="service-dropdown-container relative">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Service</label>
                      <input
                        type="text"
                        value={serviceSearch}
                        onChange={(e) => {
                          setServiceSearch(e.target.value);
                          setShowServiceDropdown(true);
                        }}
                        onFocus={() => setShowServiceDropdown(true)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search and select service..."
                        required
                      />
                      
                      {showServiceDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {services
                            ?.filter(service => 
                              service.name.toLowerCase().includes(serviceSearch.toLowerCase())
                            )
                            ?.map((service) => (
                              <button
                                key={service._id}
                                type="button"
                                onClick={() => handleServiceSelect(service)}
                                className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-800">{service.name}</div>
                                {service.description && (
                                  <div className="text-sm text-gray-500">{service.description}</div>
                                )}
                              </button>
                            ))}
                          {services?.filter(service => 
                            service.name.toLowerCase().includes(serviceSearch.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-3 text-gray-500 text-center">
                              No services found
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Sale Amount */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Sale Amount (₹)</label>
                      <input
                        type="number"
                        value={saleAmount}
                        onChange={(e) => setSaleAmount(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter sale amount"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>

                    {/* Profit */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Profit (₹)</label>
                      <input
                        type="number"
                        value={profit}
                        onChange={(e) => setProfit(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter profit amount"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex space-x-4 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowSalePopup(false)}
                        className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-green-500 to-blue-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-green-600 hover:to-blue-700 transition-all duration-200"
                        disabled={salesLoading}
                      >
                        {salesLoading ? (
                          <div className="flex items-center justify-center">
                            <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Adding...
                          </div>
                        ) : (
                          'Add Sale'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Edit Sale Modal */}
            {showEditPopup && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-800">Edit Sale</h3>
                    <button
                      onClick={() => {
                        setShowEditPopup(false);
                        setEditingSale(null);
                        setServiceId('');
                        setServiceName('');
                        setServiceSearch('');
                        setSaleAmount('');
                        setProfit('');
                      }}
                      className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                    >
                      ×
                    </button>
                  </div>
                  
                  <form onSubmit={submitEditHandler} className="space-y-4">
                    {/* Service Selection */}
                    <div className="service-dropdown-container relative">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Service</label>
                      <input
                        type="text"
                        value={serviceSearch}
                        onChange={(e) => {
                          setServiceSearch(e.target.value);
                          setShowServiceDropdown(true);
                        }}
                        onFocus={() => setShowServiceDropdown(true)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search and select service..."
                        required
                      />
                      
                      {showServiceDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {services
                            ?.filter(service => 
                              service.name.toLowerCase().includes(serviceSearch.toLowerCase())
                            )
                            ?.map((service) => (
                              <button
                                key={service._id}
                                type="button"
                                onClick={() => handleServiceSelect(service)}
                                className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-800">{service.name}</div>
                                {service.description && (
                                  <div className="text-sm text-gray-500">{service.description}</div>
                                )}
                              </button>
                            ))}
                          {services?.filter(service => 
                            service.name.toLowerCase().includes(serviceSearch.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-3 text-gray-500 text-center">
                              No services found
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Sale Amount */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Sale Amount (₹)</label>
                      <input
                        type="number"
                        value={saleAmount}
                        onChange={(e) => setSaleAmount(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter sale amount"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>

                    {/* Profit */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Profit (₹)</label>
                      <input
                        type="number"
                        value={profit}
                        onChange={(e) => setProfit(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter profit amount"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex space-x-4 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowEditPopup(false);
                          setEditingSale(null);
                          setServiceId('');
                          setServiceName('');
                          setServiceSearch('');
                          setSaleAmount('');
                          setProfit('');
                        }}
                        className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
                        disabled={salesLoading}
                      >
                        {salesLoading ? (
                          <div className="flex items-center justify-center">
                            <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Updating...
                          </div>
                        ) : (
                          'Update Sale'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Delete Sale Record</h3>
                    <p className="text-gray-600">Are you sure you want to delete this sale record? This action cannot be undone.</p>
                  </div>
                  
                  <div className="flex space-x-4">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeletingSaleId(null);
                      }}
                      className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteSale}
                      className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200"
                      disabled={salesLoading}
                    >
                      {salesLoading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Deleting...
                        </div>
                      ) : (
                        'Delete Sale'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'reports':
        return <SalesReports userRole={userInfo?.role || 'Admin'} />;
      case 'notifications':
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-center py-12">
              <FaBell className="text-6xl text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Notifications Center</h2>
              <p className="text-gray-600 mb-6">Manage system notifications and alerts</p>
              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                  <div className="flex">
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>System Update:</strong> New features have been added to the service management module.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <div className="flex">
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Reminder:</strong> Monthly backup is scheduled for tonight at 11:00 PM.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                  <div className="flex">
                    <div className="ml-3">
                      <p className="text-sm text-green-700">
                        <strong>Success:</strong> All services are running normally across both branches.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={handleTabChange} 
        userRole={userInfo?.role || 'Admin'}
      />
      
      {/* Main Content */}
      <div className="flex-1">
        {/* Header */}
        <ModernHeader user={userInfo} onLogout={handleLogout} />
        
        {/* Page Content */}
        <main className="ml-64 p-8">
          {renderContent()}
        </main>
      </div>
      

    </div>
  );
};

export default AdminDashboardScreen;

